import React from 'react';
import { Card, CardHeader } from '../../ui/Card';
import { StatusPill } from '../../ui/StatusPill';

const enrollmentLabels = {
  pending: ['Venter', 'moderate'],
  active: ['Pågår', 'healthy'],
  completed: ['Fullført', 'neutral'],
  cancelled: ['Avbrutt', 'ghost'],
  withdrawn: ['Trukket', 'ghost'],
  expired: ['Utløpt', 'ghost']
};

function formatDate(value) {
  if (!value) return '–';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '–'
    : new Intl.DateTimeFormat('nb-NO', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
}

export function AcademyParticipantDetails({ participant, onClose, onAssign }) {
  if (!participant) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40" role="presentation">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="participant-detail-title"
        className="h-full w-full max-w-2xl overflow-y-auto bg-[#f3f6fa] p-5 shadow-2xl sm:p-8"
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-blue-700">Academy · Deltaker</p>
            <h2 id="participant-detail-title" className="mt-2 text-2xl font-semibold">{participant.name}</h2>
            <p className="mt-1 text-sm text-slate-600">{participant.organizationName}</p>
            {participant.email && <p className="mt-1 text-sm text-slate-600">{participant.email}</p>}
          </div>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={onAssign}
              disabled={participant.status !== 'active'}
              className="rounded-xl bg-blue-700 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Tildel kurs
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
            >
              Lukk
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {participant.courses.length === 0 ? (
            <Card><p className="p-6 text-sm text-slate-500">Ingen kurs er registrert for deltakeren.</p></Card>
          ) : participant.courses.map((course) => {
            const [label, variant] = enrollmentLabels[course.enrollmentStatus] || [
              course.enrollmentStatus,
              'ghost'
            ];

            return (
              <Card key={course.id}>
                <CardHeader
                  title={course.courseName}
                  subtitle={course.version ? `Versjon ${course.version}` : 'Kursversjon ikke tilgjengelig'}
                />
                <div className="space-y-4 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <StatusPill label={label} variant={variant} />
                    <span className="text-sm font-semibold text-slate-700">
                      Fremdrift {course.progress}% · {course.completedUnits}/{course.requiredUnits} enheter
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-blue-600"
                      style={{ width: `${course.progress}%` }}
                    />
                  </div>
                  <dl className="grid gap-2 text-sm sm:grid-cols-2">
                    <div><dt className="text-slate-500">Påmeldt</dt><dd className="font-medium">{formatDate(course.enrolledAt)}</dd></div>
                    <div><dt className="text-slate-500">Fullført</dt><dd className="font-medium">{formatDate(course.completionDate)}</dd></div>
                  </dl>

                  <div className="border-t border-slate-100 pt-4">
                    <h3 className="font-semibold text-slate-800">Kunnskapstest</h3>
                    {course.attempts.length === 0 ? (
                      <p className="mt-2 text-sm text-slate-500">Ingen tilgjengelige testresultater.</p>
                    ) : (
                      <ul className="mt-2 space-y-2">
                        {course.attempts.map((attempt) => (
                          <li key={attempt.id} className="flex flex-wrap justify-between gap-2 text-sm">
                            <span className="text-slate-600">
                              {attempt.assessment?.title || 'Kunnskapstest'} · {formatDate(attempt.graded_at)}
                            </span>
                            <span className="font-semibold text-slate-800">
                              {attempt.score_percent == null ? 'Resultat ikke gradert' : `${attempt.score_percent}%`}
                              {attempt.passed === true ? ' · Bestått' : attempt.passed === false ? ' · Ikke bestått' : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="border-t border-slate-100 pt-4">
                    <h3 className="font-semibold text-slate-800">Kursbevis</h3>
                    {course.certificates.length === 0 ? (
                      <p className="mt-2 text-sm text-slate-500">Ingen utstedte kursbevis.</p>
                    ) : (
                      <ul className="mt-2 space-y-2">
                        {course.certificates.map((certificate) => (
                          <li key={certificate.id} className="flex flex-wrap justify-between gap-2 text-sm">
                            <span className="font-medium text-slate-700">{certificate.certificate_number}</span>
                            <time dateTime={certificate.issued_at} className="text-slate-500">
                              Utstedt {formatDate(certificate.issued_at)}
                            </time>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </section>
    </div>
  );
}